import React, { useCallback, useEffect, useMemo, useState } from "react";
import MainLayout from "../components/layout/MainLayout";
import { Card } from "../components/ui/card";
import { SMS_SCHEDULER_API_URL } from "../config";

const FLAGS: Record<string, string> = { se: "se", no: "no", fi: "fi" };

function Flag({ geo }: { geo: string }) {
  if (geo === "mixed") {
    return (
      <span className="inline-flex items-center gap-1">
        <img src="https://flagcdn.com/w20/se.png" alt="SE" className="h-3 w-[18px] inline-block rounded-sm" />
        <img src="https://flagcdn.com/w20/no.png" alt="NO" className="h-3 w-[18px] inline-block rounded-sm" />
      </span>
    );
  }
  const code = FLAGS[geo] || "se";
  return <img src={`https://flagcdn.com/w20/${code}.png`} alt={geo.toUpperCase()} className="h-3 w-[18px] inline-block rounded-sm" />;
}

const STATUS_LABEL: Record<string, { dot: string; text: string }> = {
  scheduled: { dot: "bg-emerald-500", text: "Planlagt" },
  sending: { dot: "bg-blue-500", text: "Sender..." },
  completed: { dot: "bg-gray-400", text: "Sendt" },
  paused: { dot: "bg-amber-500", text: "På pause" },
  paused_low_balance: { dot: "bg-amber-500", text: "Afventer optankning" },
  cancelled: { dot: "bg-red-400", text: "Annulleret" },
};

const PARTNER_PRESETS: Record<string, { code: string; label: string }[]> = {
  se: [
    { code: "sa-r3b", label: "Sambla SE — Direkte (/slgo/sa-r3b)" },
    { code: "le-s2b", label: "Lendo SE — Direkte (/slgo/le-s2b)" },
    { code: "ax-r3a", label: "Axo Finans SE — Direkte (/slgo/ax-r3a)" },
    { code: "zm-r3a", label: "Zmarta SE — Direkte (/slgo/zm-r3a)" },
    { code: "en-r3a", label: "Enklare SE — Direkte (/slgo/en-r3a)" },
    { code: "fe-s2a", label: "Ferratum SE — Direkte (/slgo/fe-s2a)" },
    { code: "o-r3a", label: "SimplyLoan SE Offerwall (/laneerbjudanden/)" },
  ],
  no: [
    { code: "mk-mc1", label: "Motty Kredittkort NO — Direkte (/slgo/mk-mc1)" },
    { code: "ax-r3a", label: "Axo Finans NO — Direkte (/slgo/ax-r3a)" },
    { code: "uf-r3a", label: "Uno Finans NO — Direkte (/slgo/uf-r3a)" },
    { code: "ze-r3b", label: "Zensum NO — Direkte (/slgo/ze-r3b)" },
    { code: "sa-r3a", label: "Sambla NO — Direkte (/slgo/sa-r3a)" },
    { code: "o-r3a", label: "SimplyLoan NO Offerwall (/lanetilbud/)" },
  ],
};

const FORBIDDEN_BRANDS_REGEX =
  /\b(sambla|advisa|lendo|axo|axofinans|zmarta|enklare|enkelfinans|ferratum|northmill|flexkontot|unofinans|uno finans|zensum|digifinans|heimfinans|klikklaan|klikklån|instabank|motty|plus1|trygga|loanstep)\b/i;

const FORBIDDEN_PHRASES_SE =
  /(utan uc|ingen uc|creditsafe|bisnode|svar direkt|pengarna direkt|lån direkt|betalningsanmärkning)/i;

const FORBIDDEN_PHRASES_NO =
  /(svar med en gang|svar direkte|umiddelbart svar|lån på dagen|utbetaling på dagen|utbetaling i dag|enkel søknad|enkelt å søke)/i;

function defaultNextSlotLocal(): string {
  const now = new Date();
  const d = new Date(now.getTime() + 2 * 3600 * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T15:00`;
}

export const SimplyloanSmsContent: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"upcoming" | "sent" | "create" | "pools">("upcoming");

  const [editingWaveId, setEditingWaveId] = useState<number | null>(null);
  const [editTimeLocal, setEditTimeLocal] = useState<string>("");
  const [previewWaveId, setPreviewWaveId] = useState<number | null>(null);

  // Create Wave form state
  const [newName, setNewName] = useState("Lørdag Gold SE");
  const [newGeo, setNewGeo] = useState<"se" | "no">("se");
  const [newPool, setNewPool] = useState("GOLD_VERIFIED_OWNER");
  const [newVariant, setNewVariant] = useState("sa-r3b");
  const [newCount, setNewCount] = useState<number>(200);
  const [newScheduledLocal, setNewScheduledLocal] = useState<string>(defaultNextSlotLocal);
  const [newNotes, setNewNotes] = useState("");
  const [includeRepExample, setIncludeRepExample] = useState(false);
  const [useCustomCopy, setUseCustomCopy] = useState(false);
  const [customTemplate, setCustomTemplate] = useState(
    "Hej!\nFlera långivare väntar på din ansökan.\n\nSe nya möjligheter och ansök:\n{link}\n\nAvreg: {unsub}"
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${SMS_SCHEDULER_API_URL}/api/simplyloan/sms`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (e: any) {
      setError(e?.message || "Kunne ikke hente SMS-data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runWaveAction = async (payload: Record<string, any>, successMsg: string) => {
    setActionBusy(JSON.stringify(payload));
    setError(null);
    setToast(null);
    try {
      const res = await fetch(`${SMS_SCHEDULER_API_URL}/api/simplyloan/sms`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok || json.ok === false) {
        throw new Error(json.error || json.reason || `HTTP ${res.status}`);
      }
      setToast(successMsg);
      setEditingWaveId(null);
      await load();
    } catch (e: any) {
      setError(e?.message || "Handling fejlede");
    } finally {
      setActionBusy(null);
    }
  };

  const handleCreateWave = async (e: React.FormEvent) => {
    e.preventDefault();
    const repTemplate =
      newGeo === "se"
        ? "Jämför lån upp till 600.000 kr:\n{link}\nEx:100k/5år, eff.10,9%, tot 129.500\nAvreg: {unsub}"
        : "Sammenlign lån opptil 600.000 kr:\n{link}\nEks:100k o/5år, eff.13,9%, tot 137.900\nStopp: {unsub}";
    await runWaveAction(
      {
        action: "create_wave",
        name: newName,
        geo: newGeo,
        target_pool: newPool,
        variant_code: newVariant,
        count: newCount,
        scheduled_for_local: newScheduledLocal,
        notes: newNotes,
        custom_template: useCustomCopy ? customTemplate : includeRepExample ? repTemplate : "",
      },
      `Kampagnen "${newName}" er planlagt.`
    );
    setActiveTab("upcoming");
  };

  // Sort upcoming waves chronologically (earliest / next due at the top)
  const upcomingWaves = useMemo(() => {
    const list = (data?.waves || []).filter((w: any) =>
      ["scheduled", "sending", "paused", "paused_low_balance"].includes(w.status)
    );
    return [...list].sort((a, b) =>
      String(a.scheduled_for_utc || "").localeCompare(String(b.scheduled_for_utc || ""))
    );
  }, [data]);

  // Sort sent waves newest first
  const sentWaves = useMemo(
    () => (data?.waves || []).filter((w: any) => w.status === "completed" || w.sent_count > 0),
    [data]
  );

  const availableInSelectedPool = useMemo(() => {
    if (!data?.pools) return 0;
    const found = data.pools.find((p: any) => p.geo === newGeo && p.quality_bucket === newPool);
    return found ? found.available : 0;
  }, [data, newGeo, newPool]);

  const livePreviewText = useMemo(() => {
    const domain = newGeo === "se" ? "simplyloan.se" : "simplyloan.no";
    const sampleCid = "105432";
    const link = `https://${domain}/slgo/${newVariant}-${sampleCid}`;
    const unsub = `https://${domain}/slgo/u-${sampleCid}`;
    if (useCustomCopy) {
      return customTemplate.replace("{link}", link).replace("{unsub}", unsub).replace("{cid}", sampleCid);
    }
    if (includeRepExample) {
      if (newGeo === "se") {
        return `Jämför lån upp till 600.000 kr:\n${link}\nEx:100k/5år, eff.10,9%, tot 129.500\nAvreg: ${unsub}`;
      }
      return `Sammenlign lån opptil 600.000 kr:\n${link}\nEks:100k o/5år, eff.13,9%, tot 137.900\nStopp: ${unsub}`;
    }
    if (newGeo === "se") {
      if (newVariant.startsWith("fe-")) {
        return `Hej!\nBehöver du fortfarande se över dina möjligheter?\n\nSe ditt uppdaterade alternativ här:\n${link}\n\nAvreg: ${unsub}`;
      }
      if (newVariant.startsWith("le-")) {
        return `Hej!\nVi har uppdaterat dina möjligheter.\n\nSe och jämför dina alternativ här:\n${link}\n\nAvreg: ${unsub}`;
      }
      return `Hej!\nFlera långivare väntar på din ansökan.\n\nSe nya möjligheter och ansök:\n${link}\n\nAvreg: ${unsub}`;
    }
    if (newPool === "STEP2_CLICKERS_AND_REJECTED") {
      return `Hei!\nVi har oppdatert dine muligheter.\n\nSe dine alternativer her:\n${link}\n\nStopp: ${unsub}`;
    }
    return `Hei!\nFlere långivere venter på din søknad.\n\nSjekk nye muligheter og søk her:\n${link}\n\nStopp: ${unsub}`;
  }, [newGeo, newPool, newVariant, includeRepExample, useCustomCopy, customTemplate]);

  const complianceChecks = useMemo(() => {
    const bodyNoUrls = livePreviewText.replace(/https?:\/\/\S+|\bsimplyloan\.(?:se|no|dk)\/\S+/gi, "");
    const bLower = bodyNoUrls.toLowerCase();
    const lenOk = livePreviewText.length <= 160;
    const unsubOk = livePreviewText.includes("/slgo/u-");
    const hasNumbersOrPct = /\d/.test(bodyNoUrls) || bodyNoUrls.includes("%");
    const hasShortRepEx =
      /\b(ex|eks|eksempel|exempel)\b/i.test(bLower) &&
      bLower.includes("%") &&
      (bLower.includes("eff") || bLower.includes("rente") || bLower.includes("ränta")) &&
      bLower.includes("tot");
    const repRuleOk = !hasNumbersOrPct || hasShortRepEx;
    const noBrandOk = !FORBIDDEN_BRANDS_REGEX.test(bodyNoUrls) || hasShortRepEx;
    const noForbiddenPhraseOk =
      newGeo === "se" ? !FORBIDDEN_PHRASES_SE.test(bodyNoUrls) : !FORBIDDEN_PHRASES_NO.test(bodyNoUrls);

    return {
      allValid: lenOk && unsubOk && repRuleOk && noBrandOk && noForbiddenPhraseOk,
      items: [
        {
          ok: lenOk,
          label: "Maks 160 tegn (1 SMS-segment)",
        },
        {
          ok: repRuleOk,
          label: hasNumbersOrPct
            ? "Forkortet renteeksempel inkluderet (Ex:/Eks: + eff.% + tot)"
            : "Ingen tal/beløb/% i teksten (eller tilføj forkortet renteeksempel)",
        },
        {
          ok: noBrandOk,
          label: "Neutral SimplyLoan-afsender (eller banknavn med renteeksempel)",
        },
        {
          ok: noForbiddenPhraseOk,
          label:
            newGeo === "se"
              ? 'Ingen "utan UC" / "svar direkt" (Konsumentkreditlagen 6 a §)'
              : 'Ingen "svar med en gang" / "lån på dagen" (Finansavtaleforskriften § 3-5)',
        },
        {
          ok: unsubOk,
          label: "Afmeldingslink (/slgo/u-{cid}) inkluderet",
        },
      ],
    };
  }, [livePreviewText, newGeo]);

  const creditDkk = data?.gatewayapi?.credit_dkk ?? 0;
  const totals = data?.totals || {};

  // Compute cumulative cost across upcoming waves so balance coverage is accurate
  const upcomingWithCumulative = useMemo(() => {
    let running = 0;
    return upcomingWaves.map((w: any) => {
      running += Number(w.est_cost_dkk || 0);
      const shortfall = Math.max(0, running - creditDkk);
      return { ...w, cumulativeCost: running, shortfall };
    });
  }, [upcomingWaves, creditDkk]);

  return (
    <div className="space-y-6">
      {/* Clean Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SimplyLoan SMS</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Kører automatisk i AWS hvert 5. minut · GatewayAPI saldo:{" "}
            <span className="font-medium text-gray-800">{Number(creditDkk).toFixed(2)} DKK</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => runWaveAction({ action: "sync_dlr" }, "Leveringsstatus opdateret")}
            disabled={!!actionBusy}
            className="text-xs px-3 py-1.5 bg-white border border-gray-200 hover:bg-gray-50 rounded-md text-gray-700 disabled:opacity-50"
          >
            Sync DLR
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700 disabled:opacity-50"
          >
            {loading ? "Indlæser…" : "↻ Opdater"}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-3.5 py-2.5 text-xs text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="underline ml-4">
            Luk
          </button>
        </div>
      )}

      {toast && (
        <div className="rounded-md bg-gray-900 text-white px-3.5 py-2.5 text-xs flex items-center justify-between">
          <span>{toast}</span>
          <button onClick={() => setToast(null)} className="underline text-gray-300 ml-4">
            Luk
          </button>
        </div>
      )}

      {/* Clean Minimalist KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card className="p-3.5 shadow-sm">
          <div className="text-xs text-gray-500">Saldo</div>
          <div className="text-lg font-semibold mt-1 text-gray-900">{Number(creditDkk).toFixed(2)} kr.</div>
          <div className="text-xs text-gray-400 mt-0.5">GatewayAPI</div>
        </Card>

        <Card className="p-3.5 shadow-sm">
          <div className="text-xs text-gray-500">Leveret</div>
          <div className="text-lg font-semibold mt-1 text-gray-900">
            {totals.delivered ?? 0} <span className="text-xs font-normal text-gray-400">/ {totals.sent ?? 0}</span>
          </div>
          <div className="text-xs text-gray-400 mt-0.5">{totals.delivery_rate_pct ?? 0}% leveret</div>
        </Card>

        <Card className="p-3.5 shadow-sm">
          <div className="text-xs text-gray-500">Unikke klik</div>
          <div className="text-lg font-semibold mt-1 text-gray-900">
            {totals.unique_clickers ?? 0}{" "}
            <span className="text-xs font-normal text-gray-500">({totals.ctr_pct ?? 0}%)</span>
          </div>
          <div className="text-xs text-gray-400 mt-0.5">CTR af leverede</div>
        </Card>

        <Card className="p-3.5 shadow-sm">
          <div className="text-xs text-gray-500">Ansøgninger</div>
          <div className="text-lg font-semibold mt-1 text-gray-900">{totals.applications ?? 0}</div>
          <div className="text-xs text-gray-400 mt-0.5">{totals.paid_loans ?? 0} udbetalt</div>
        </Card>

        <Card className="p-3.5 shadow-sm">
          <div className="text-xs text-gray-500">Omsætning</div>
          <div className="text-lg font-semibold mt-1 text-gray-900">
            {(totals.revenue_dkk ?? 0).toLocaleString("da-DK")} kr.
          </div>
          <div className="text-xs text-gray-400 mt-0.5">Spend: {(totals.spend_dkk ?? 0).toFixed(0)} kr.</div>
        </Card>

        <Card className="p-3.5 shadow-sm">
          <div className="text-xs text-gray-500">Netto resultat</div>
          <div className="text-lg font-semibold mt-1 text-gray-900">
            +{(totals.net_profit_dkk ?? 0).toLocaleString("da-DK")} kr.
          </div>
          <div className="text-xs text-gray-400 mt-0.5">
            {totals.roas ?? 0}x ROAS · {data?.circuit_breaker?.optout_rate_pct ?? 0}% afmeldt
          </div>
        </Card>
      </div>

      {/* Simple Text Sub-Navigation */}
      <div className="border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-6">
          {[
            { id: "upcoming", label: `Planlagte (${upcomingWaves.length})` },
            { id: "sent", label: `Sendte (${sentWaves.length})` },
            { id: "pools", label: "Nummer-puljer" },
            { id: "create", label: "+ Ny bølge" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id as any)}
              className={`py-2.5 text-sm border-b-2 -mb-px transition-colors ${
                activeTab === t.id
                  ? "border-gray-900 font-semibold text-gray-900"
                  : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* TAB 1: UPCOMING WAVES (CLEAN TABLE) */}
      {activeTab === "upcoming" && (
        <Card className="shadow-sm overflow-hidden">
          {upcomingWithCumulative.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">
              Ingen planlagte bølger i køen.{" "}
              <button onClick={() => setActiveTab("create")} className="underline text-gray-900 font-medium">
                Opret ny bølge
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50/60 text-xs text-gray-500">
                    <th className="py-2.5 px-4 text-left font-medium">Tidspunkt</th>
                    <th className="py-2.5 px-3 text-left font-medium">Kampagne</th>
                    <th className="py-2.5 px-3 text-left font-medium">Variants</th>
                    <th className="py-2.5 px-3 text-right font-medium">Antal</th>
                    <th className="py-2.5 px-3 text-right font-medium">Est. pris</th>
                    <th className="py-2.5 px-3 text-left font-medium">Status</th>
                    <th className="py-2.5 px-4 text-right font-medium">Handlinger</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {upcomingWithCumulative.map((w: any) => {
                    const st = STATUS_LABEL[w.status] || STATUS_LABEL.scheduled;
                    const isEditing = editingWaveId === w.id;
                    const isPreviewOpen = previewWaveId === w.id;

                    return (
                      <React.Fragment key={w.id}>
                        <tr className="hover:bg-gray-50/60">
                          <td className="py-3 px-4 whitespace-nowrap text-gray-900">
                            <span className="inline-flex items-center gap-2">
                              <Flag geo={w.geo} />
                              <span className="font-medium">{w.scheduled_display}</span>
                            </span>
                          </td>
                          <td className="py-3 px-3 whitespace-nowrap font-medium text-gray-900">
                            {w.name.replace(/^.*?—\s*/, "")}
                          </td>
                          <td className="py-3 px-3 whitespace-nowrap text-xs text-gray-500 font-mono">
                            {w.variant_code}
                          </td>
                          <td className="py-3 px-3 text-right whitespace-nowrap font-medium text-gray-900">
                            {w.total_queued}
                          </td>
                          <td className="py-3 px-3 text-right whitespace-nowrap">
                            <span className="text-gray-900">{w.est_cost_dkk.toFixed(2)} kr.</span>
                            {w.shortfall > 0 && (
                              <span className="block text-[11px] text-amber-700">
                                Mangler {w.shortfall.toFixed(0)} kr.
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-3 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1.5 text-xs text-gray-700">
                              <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                              {st.text}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right whitespace-nowrap">
                            <div className="inline-flex items-center gap-1.5 text-xs">
                              <button
                                onClick={() => setPreviewWaveId(isPreviewOpen ? null : w.id)}
                                className="px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 text-gray-700"
                              >
                                {isPreviewOpen ? "Luk" : "Vis SMS"}
                              </button>
                              <button
                                onClick={() => {
                                  setEditingWaveId(isEditing ? null : w.id);
                                  setEditTimeLocal(w.scheduled_for_local || "");
                                }}
                                className="px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 text-gray-700"
                              >
                                Tidspunkt
                              </button>
                              {w.status === "paused" || w.status === "paused_low_balance" ? (
                                <button
                                  onClick={() =>
                                    runWaveAction({ action: "resume", wave_id: w.id }, "Bølge genoptaget")
                                  }
                                  disabled={!!actionBusy}
                                  className="px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 text-gray-700"
                                >
                                  Genoptag
                                </button>
                              ) : (
                                <button
                                  onClick={() =>
                                    runWaveAction({ action: "pause", wave_id: w.id }, "Bølge sat på pause")
                                  }
                                  disabled={!!actionBusy}
                                  className="px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 text-gray-700"
                                >
                                  Pause
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  if (window.confirm(`Send "${w.name}" (${w.total_queued} SMS) nu?`)) {
                                    runWaveAction({ action: "trigger_now", wave_id: w.id }, "Afsendelse startet");
                                  }
                                }}
                                disabled={!!actionBusy}
                                className="px-2.5 py-1 rounded bg-gray-900 hover:bg-gray-800 text-white font-medium"
                              >
                                Send nu
                              </button>
                              <button
                                onClick={() => {
                                  if (window.confirm(`Annuller "${w.name}"?`)) {
                                    runWaveAction({ action: "cancel", wave_id: w.id }, "Bølge annulleret");
                                  }
                                }}
                                disabled={!!actionBusy}
                                className="px-2 py-1 text-gray-400 hover:text-red-600"
                                title="Annuller"
                              >
                                ×
                              </button>
                            </div>
                          </td>
                        </tr>

                        {isEditing && (
                          <tr className="bg-gray-50/80">
                            <td colSpan={7} className="px-4 py-2.5">
                              <div className="flex items-center gap-3 text-xs">
                                <span className="text-gray-600">Nyt tidspunkt (dansk tid):</span>
                                <input
                                  type="datetime-local"
                                  value={editTimeLocal}
                                  onChange={(e) => setEditTimeLocal(e.target.value)}
                                  className="border border-gray-300 rounded px-2 py-1 text-xs bg-white"
                                />
                                <button
                                  onClick={() =>
                                    runWaveAction(
                                      { action: "reschedule", wave_id: w.id, scheduled_for_local: editTimeLocal },
                                      "Tidspunkt opdateret"
                                    )
                                  }
                                  className="px-3 py-1 rounded bg-gray-900 text-white font-medium"
                                >
                                  Gem
                                </button>
                                <button onClick={() => setEditingWaveId(null)} className="text-gray-500">
                                  Annuller
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}

                        {isPreviewOpen && (
                          <tr className="bg-gray-50/60">
                            <td colSpan={7} className="px-4 py-3">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                <div>
                                  <div className="text-gray-500 mb-1">
                                    SMS-tekst ({(w.sample_sms || "").length}/160 tegn):
                                  </div>
                                  <pre className="bg-white border border-gray-200 rounded p-2.5 font-mono whitespace-pre-wrap text-gray-800">
                                    {w.sample_sms}
                                  </pre>
                                </div>
                                <div>
                                  <div className="text-gray-500 mb-1">Modtager-fordeling:</div>
                                  <div className="bg-white border border-gray-200 rounded divide-y divide-gray-100">
                                    {(w.breakdown || []).map((b: any, i: number) => (
                                      <div key={i} className="px-3 py-1.5 flex justify-between">
                                        <span>
                                          <Flag geo={b.geo} /> <code className="ml-1">{b.variant_code}</code>{" "}
                                          <span className="text-gray-400">({b.segment})</span>
                                        </span>
                                        <span className="font-medium">{b.count} SMS</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* TAB 2: SENT CAMPAIGNS & VARIANT BREAKDOWN */}
      {activeTab === "sent" && (
        <div className="space-y-6">
          <Card className="shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <h2 className="font-semibold text-sm text-gray-900">Sendte bølger</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50/60 text-xs text-gray-500">
                    <th className="py-2 px-4 text-left font-medium">Sendt</th>
                    <th className="py-2 px-3 text-left font-medium">Bølge</th>
                    <th className="py-2 px-3 text-right font-medium">Leveret</th>
                    <th className="py-2 px-3 text-right font-medium">Klik (CTR)</th>
                    <th className="py-2 px-3 text-right font-medium">Ansøgninger</th>
                    <th className="py-2 px-3 text-right font-medium">Udbetalt</th>
                    <th className="py-2 px-3 text-right font-medium">Spend</th>
                    <th className="py-2 px-4 text-right font-medium">Omsætning</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sentWaves.map((w: any) => {
                    const delivPct = w.sent_count > 0 ? ((w.delivered_count / w.sent_count) * 100).toFixed(1) : "0.0";
                    return (
                      <tr key={w.id} className="hover:bg-gray-50/60">
                        <td className="py-2.5 px-4 whitespace-nowrap text-xs text-gray-500">{w.scheduled_display}</td>
                        <td className="py-2.5 px-3">
                          <div className="font-medium text-gray-900 flex items-center gap-2">
                            <Flag geo={w.geo} />
                            <span>{w.name}</span>
                          </div>
                        </td>
                        <td className="py-2.5 px-3 text-right whitespace-nowrap">
                          {w.delivered_count} / {w.sent_count}{" "}
                          <span className="text-xs text-gray-400">({delivPct}%)</span>
                        </td>
                        <td className="py-2.5 px-3 text-right whitespace-nowrap">
                          {w.unique_clickers} <span className="text-xs text-gray-400">({w.ctr_pct}%)</span>
                        </td>
                        <td className="py-2.5 px-3 text-right whitespace-nowrap">
                          {w.applications}
                          {w.applications > 0 && (
                            <span className="text-xs text-gray-400 ml-1">
                              ({w.approved_uc} OK / {w.rejected_uc} F)
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-right whitespace-nowrap">{w.paid_loans}</td>
                        <td className="py-2.5 px-3 text-right whitespace-nowrap text-gray-600">
                          {w.actual_cost_dkk.toFixed(2)} kr.
                        </td>
                        <td className="py-2.5 px-4 text-right whitespace-nowrap font-medium text-gray-900">
                          {w.revenue_dkk.toLocaleString("da-DK")} kr.
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <h2 className="font-semibold text-sm text-gray-900">Performance pr. variant</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50/60 text-xs text-gray-500">
                    <th className="py-2 px-4 text-left font-medium">Variant</th>
                    <th className="py-2 px-3 text-left font-medium">Sendt</th>
                    <th className="py-2 px-3 text-right font-medium">Leveret</th>
                    <th className="py-2 px-3 text-right font-medium">Fejlet</th>
                    <th className="py-2 px-3 text-right font-medium">Klik (CTR)</th>
                    <th className="py-2 px-3 text-right font-medium">Ansøgninger</th>
                    <th className="py-2 px-3 text-right font-medium">Spend</th>
                    <th className="py-2 px-4 text-right font-medium">Omsætning</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(data?.variants || []).map((v: any) => (
                    <tr key={`${v.geo}-${v.variant_code}`} className="hover:bg-gray-50/60">
                      <td className="py-2 px-4 whitespace-nowrap flex items-center gap-2">
                        <Flag geo={v.geo} />
                        <code className="text-xs font-medium text-gray-800">{v.variant_code}</code>
                      </td>
                      <td className="py-2 px-3 text-xs text-gray-500 whitespace-nowrap">{v.sent_at_display}</td>
                      <td className="py-2 px-3 text-right whitespace-nowrap">
                        {v.delivered} / {v.sent} <span className="text-xs text-gray-400">({v.delivery_rate_pct}%)</span>
                      </td>
                      <td className="py-2 px-3 text-right whitespace-nowrap text-xs text-gray-500">{v.failed}</td>
                      <td className="py-2 px-3 text-right whitespace-nowrap">
                        {v.unique_clickers} <span className="text-xs text-gray-400">({v.ctr_pct}%)</span>
                      </td>
                      <td className="py-2 px-3 text-right whitespace-nowrap">
                        {v.applications}
                        {v.applications > 0 && (
                          <span className="text-xs text-gray-400 ml-1">
                            ({v.approved_uc} OK / {v.rejected_uc} F)
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-right whitespace-nowrap text-xs text-gray-600">
                        {v.spend_dkk.toFixed(2)} kr.
                      </td>
                      <td className="py-2 px-4 text-right whitespace-nowrap font-medium text-gray-900">
                        {v.revenue_dkk.toFixed(0)} kr.
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* TAB 3: CREATE NEW WAVE */}
      {activeTab === "create" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="p-5 shadow-sm lg:col-span-2">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Planlæg ny SMS-bølge</h3>
            <form onSubmit={handleCreateWave} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Navn</label>
                  <input
                    type="text"
                    required
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Tidspunkt (dansk tid)</label>
                  <input
                    type="datetime-local"
                    required
                    value={newScheduledLocal}
                    onChange={(e) => setNewScheduledLocal(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Marked</label>
                  <select
                    value={newGeo}
                    onChange={(e) => {
                      const g = e.target.value as "se" | "no";
                      setNewGeo(g);
                      setNewVariant(g === "se" ? "sa-r3b" : "ax-r3a");
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                  >
                    <option value="se">Sverige (SE)</option>
                    <option value="no">Norge (NO)</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs text-gray-600 mb-1">
                    Målgruppe ({availableInSelectedPool} ledige)
                  </label>
                  <select
                    value={newPool}
                    onChange={(e) => setNewPool(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                  >
                    <option value="GOLD_VERIFIED_OWNER">GOLD_VERIFIED_OWNER (Hitta.se / 1881.no navnematch)</option>
                    <option value="STEP2_CLICKERS_AND_REJECTED">Step 2 Opfølgning (Klikkere & Score=F afviste)</option>
                    <option value="SILVER_UNLISTED_ACTIVE_OR_RECENT">SILVER_UNLISTED (Ulistede mobilnumre)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Partner / Variant</label>
                  <select
                    value={newVariant}
                    onChange={(e) => setNewVariant(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                  >
                    {(PARTNER_PRESETS[newGeo] || []).map((p) => (
                      <option key={p.code} value={p.code}>
                        {p.label} ({p.code})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Antal SMS ({(newCount * (newGeo === "se" ? 0.315 : 0.3995)).toFixed(2)} kr.)
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={1000}
                    value={newCount}
                    onChange={(e) => setNewCount(Number(e.target.value))}
                    className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-5">
                  <label className="inline-flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeRepExample}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setIncludeRepExample(checked);
                        if (checked && useCustomCopy) {
                          setCustomTemplate(
                            newGeo === "se"
                              ? "Jämför lån upp till 600.000 kr:\n{link}\nEx:100k/5år, eff.10,9%, tot 129.500\nAvreg: {unsub}"
                              : "Sammenlign lån opptil 600.000 kr:\n{link}\nEks:100k o/5år, eff.13,9%, tot 137.900\nStopp: {unsub}"
                          );
                        }
                      }}
                    />
                    Inkluder forkortet renteeksempel i SMS (`Ex:100k/5år...`)
                  </label>
                  <label className="inline-flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useCustomCopy}
                      onChange={(e) => setUseCustomCopy(e.target.checked)}
                    />
                    Tilpas SMS-tekst manuelt
                  </label>
                </div>
                {useCustomCopy && (
                  <textarea
                    rows={5}
                    value={customTemplate}
                    onChange={(e) => setCustomTemplate(e.target.value)}
                    className="mt-2 w-full border border-gray-300 rounded-md p-2.5 text-xs font-mono"
                  />
                )}
              </div>

              <div className="pt-2 flex justify-end">
                <button
                  type="submit"
                  disabled={!!actionBusy || !complianceChecks.allValid}
                  className="px-4 py-2 rounded-md text-xs font-semibold bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
                >
                  Planlæg bølge
                </button>
              </div>
            </form>
          </Card>

          <Card className="p-5 shadow-sm space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-600">SMS Preview</span>
                <span className="text-xs text-gray-400 font-mono">{livePreviewText.length} / 160 tegn</span>
              </div>
              <pre className="text-xs font-mono whitespace-pre-wrap text-gray-800 bg-gray-50 border border-gray-200 rounded-md p-3">
                {livePreviewText}
              </pre>
            </div>

            <div className="border-t border-gray-100 pt-3">
              <div className="text-xs font-medium text-gray-700 mb-2">
                Faste SMS-regler ({newGeo.toUpperCase()})
              </div>
              <ul className="space-y-1.5 text-xs">
                {complianceChecks.items.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className={item.ok ? "text-emerald-600 font-semibold" : "text-red-600 font-semibold"}>
                      {item.ok ? "✓" : "✕"}
                    </span>
                    <span className={item.ok ? "text-gray-600" : "text-red-700 font-medium"}>{item.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Card>
        </div>
      )}

      {/* TAB 4: DIRECTORY POOLS */}
      {activeTab === "pools" && (
        <Card className="shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50/60 text-xs text-gray-500">
                  <th className="py-2 px-4 text-left font-medium">Land</th>
                  <th className="py-2 px-3 text-left font-medium">Pulje</th>
                  <th className="py-2 px-3 text-right font-medium">Total</th>
                  <th className="py-2 px-4 text-right font-medium">Ledige (ikke sendt / ikke i kø)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(data?.pools || []).map((p: any, i: number) => {
                  const isDrop = p.quality_bucket.startsWith("DROP_");
                  return (
                    <tr key={i} className="hover:bg-gray-50/60">
                      <td className="py-2.5 px-4">
                        <Flag geo={p.geo} /> <span className="ml-1 uppercase text-xs">{p.geo}</span>
                      </td>
                      <td className="py-2.5 px-3 font-mono text-xs text-gray-800">{p.quality_bucket}</td>
                      <td className="py-2.5 px-3 text-right text-gray-600">{p.total}</td>
                      <td className="py-2.5 px-4 text-right font-medium text-gray-900">
                        {isDrop ? <span className="text-gray-400 text-xs">Ekskluderet</span> : p.available}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

const SimplyloanSmsPage: React.FC = () => {
  return (
    <MainLayout>
      <div className="p-6 max-w-6xl mx-auto">
        <SimplyloanSmsContent />
      </div>
    </MainLayout>
  );
};

export default SimplyloanSmsPage;
